import {useRecordCapabilitiesQuery} from './useDnsRecordsQuery';
import {RecordWriteCapabilities} from '../services/DnsRecordsService';
import {COMMON_RECORD_TYPES} from '../types';

// FNP-1192 — THE CAPABILITY-DRIVEN HIDING DECISION, and the type picker's source.
//
// Follows `zones/hooks/useZoneWriteCapabilities` exactly — same query, same
// default, same reasoning — because the rule is the same rule and two modules
// deriving it differently is how they end up disagreeing.
//
// ⚠️ THE DEFAULT IS "NOT PERMITTED", IN EVERY UNKNOWN CASE.
//
// Loading, errored, connection missing, connection not ACTIVE, capability
// snapshot absent — all of them hide the control. The alternative fails in the
// worse direction: a control that appears optimistically and then errors at
// submit tells a tenant their read-only credential is broken, when the truth is
// that Flexi should never have offered the action. An absent button is a
// correct statement about a read-only connection; a failing one is not.
//
// The catalog entry (FNP-1179) is the CEILING and this is that ceiling narrowed
// by what the credential actually proved at connect time. A tenant who handed
// Flexi a read-only Route 53 role has canWriteRecords: false even though the
// Route 53 catalog entry says true.
export const useRecordWriteCapabilities = (connectionId?: string) => {

    const {
        data: capabilities,
        isLoading,
        isError,
    } = useRecordCapabilitiesQuery(connectionId);

    // The query resolves null for "no snapshot" (React Query v5 forbids
    // undefined); both mean the same thing to every caller below.
    const resolved: RecordWriteCapabilities | undefined = capabilities ?? undefined;

    // Not merely `!capabilities`: a snapshot that arrived is still unusable
    // while the query is in flight or after it failed.
    const known = Boolean(connectionId) && !isLoading && !isError && Boolean(resolved);

    // ⚠️ THE TYPES THE FORM MAY OFFER.
    //
    // The connection's own list wins wherever it reported one — that is the
    // whole point of `supportedRecordTypes`, and offering a type this provider
    // rejects produces a failure at submit that the form could have prevented.
    //
    // The fallback is used ONLY when the snapshot reported an EMPTY list, which
    // means the connection did not enumerate its types rather than that it
    // supports none. A provider that genuinely supported no record types could
    // not serve DNS at all, so reading empty as "none" would leave the form
    // with an empty picker and no way to explain itself.
    const supportedRecordTypes = known && resolved!.supportedRecordTypes.length > 0
        ? resolved!.supportedRecordTypes
        : COMMON_RECORD_TYPES;

    // Whether the picker is showing what the PROVIDER said or a generic list.
    // The form says which, so an operator who cannot find a type knows whether
    // to blame their provider or Flexi.
    const supportedTypesAreProviderReported =
        known && resolved!.supportedRecordTypes.length > 0;

    return {
        capabilities: resolved,
        // True only when Flexi actually knows what this credential can do.
        capabilitiesKnown: known,
        loading: Boolean(connectionId) && isLoading,
        // What every write control in this module gates on.
        canWriteRecords: known && resolved!.canWriteRecords === true,
        supportedRecordTypes,
        supportedTypesAreProviderReported,
    };
};
